import { type AutocompletePlace } from "screens/destination/utils";
import {
  HIDDEN_PLACE_IDS,
  UNSUPPORTED_COUNTRIES,
  UNSUPPORTED_PLACE_IDS,
} from "settings";
import { isIsraelTrip } from "utils/google-places/destination/is-israel-trip";
import { googlePlacesAutocomplete } from "./autocomplete";
import { googlePlacesGeocode } from "./geocode";

const LOCATION_CODE_SEARCH_PARAM_KEY = "dest";
const PLACE_ID_SEARCH_PARAM_KEY = "destp";

async function getGooglePlaceFromIsoCode(iso3166: string) {
  const [country, region] = iso3166.split("-");

  return (
    await googlePlacesAutocomplete({
      countries: [country],
      input: iso3166,
      types: [region ? "(regions)" : "country"],
    })
  )?.[0];
}

function isUnsupportedPlace(country?: string, placeId?: string): boolean {
  return Boolean(
    (country && UNSUPPORTED_COUNTRIES.includes(country)) ||
      (placeId &&
        (UNSUPPORTED_PLACE_IDS.includes(placeId) ||
          HIDDEN_PLACE_IDS.includes(placeId))),
  );
}

function removeDuplicatePlaces(
  places: AutocompletePlace[],
): AutocompletePlace[] {
  return places.reduce<AutocompletePlace[]>((unique, current) => {
    return unique.some((place) => place.placeId === current.placeId)
      ? unique
      : [...unique, current];
  }, []);
}

interface RawPlace {
  description?: string;
  formatted_address?: string;
  place_id: string;
  terms: Array<{ value: string; offset: number }>;
  types: string[];
  error?: boolean;
}

function transformToAutocompletePlace(
  place: RawPlace,
  fallbackLabel: string,
): AutocompletePlace {
  const label = place.description ?? place.formatted_address ?? fallbackLabel;

  const transformed: AutocompletePlace = {
    label,
    description: label,
    placeId: place.place_id,
    types: place.types,
    terms: place.terms ?? [],
    error: place.error ?? false,
    warning: false,
    disabled: false,
  };

  const country =
    transformed.terms?.[transformed.terms.length - 1]?.value?.toUpperCase();

  transformed.error = isUnsupportedPlace(country, transformed.placeId);
  transformed.warning = isIsraelTrip([transformed]);

  return transformed;
}

export async function getGooglePlacesFromSearchParams(
  searchParams?: URLSearchParams,
) {
  if (!searchParams) {
    searchParams = new URLSearchParams(window.location.search);
  }
  const locationCodes = searchParams.getAll(LOCATION_CODE_SEARCH_PARAM_KEY);
  const placeIds = searchParams.getAll(PLACE_ID_SEARCH_PARAM_KEY);

  const allPlaces = [...locationCodes, ...placeIds];

  if (!allPlaces.length) {
    return [];
  }

  const placePromises = await Promise.allSettled([
    ...locationCodes.map(getGooglePlaceFromIsoCode),
    ...placeIds.map(async (placeId) => await googlePlacesGeocode(placeId)),
  ]);

  const places = placePromises.map((result, index) => {
    const place =
      result.status === "fulfilled"
        ? result.value
        : ({
            description: allPlaces[index],
            place_id: "",
            terms: [],
            types: [],
            error: true,
          } as RawPlace);

    return transformToAutocompletePlace(place as RawPlace, allPlaces[index]);
  });

  return removeDuplicatePlaces(places);
}
